import { MunicipalityRestriction } from '@app/shared/services/api/municipality-restrictions.service';
import { MapStoreMunicipality } from '@app/shared/models/municipality/Municipality';
import { Injectable, OnDestroy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { FeaturePermission, MunicipalityRestrictionsMapping, PermissionService } from '@app/shared/services/api/permission.service';
import { StateService } from '@app/shared/services/state/state.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { cloneDeep, isEmpty, isNil, orderBy } from 'lodash';
import { BehaviorSubject, catchError, first, map, Observable, Subscription, switchMap, tap, throwError, forkJoin, withLatestFrom } from 'rxjs';
import { PermissionStatus, PermissionStatusCode } from './../models/externe-apps';
import { externeAppsDefaultStatus } from '../models/externe-apps.constants';

@Injectable({
  providedIn: 'root',
})
export class ExterneAppsStateService extends StateService<ExterneAppsState> implements OnDestroy {
  protected externalAppStateSubscription$: Subscription | null = null;
  protected featurePermissionsSubject$: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);

  public featurePermissionStateIsLoading$: Observable<boolean> = this.select((state: ExterneAppsState) => state.loading);

  public featurePermissions$: Observable<FeaturePermission[]> = this.select((state: ExterneAppsState) => state.values);

  public iconList$: Observable<string[]> = this.select((state: ExterneAppsState) => {
    let iconList: string[] = [];
    state.values.forEach((featurePermission) => {
      const iconSrc = featurePermission.iconName.split(';');
      iconSrc.forEach((icon) => {
        if (icon.startsWith('upload_')) {
          iconList.push(icon);
        }
      });
    });
    return iconList;
  });

  constructor(private permissionService: PermissionService, private userMunicipalitiesService: UserMunicipalitiesService) {
    super(initialExterneAppsState);
  }

  public ngOnDestroy(): void {
    this.externalAppStateSubscription$?.unsubscribe();
  }

  public clear(): void {
    this.setState(initialExterneAppsState);
  }

  private beginLoading(): void {
    this.setState({ ...this.state, loading: true, values: [] });
  }

  public endLoading(): void {
    this.setState({ ...this.state, loading: false });
  }

  public dispatch(municipalityGuid?: string): void {
    this.clear();
    this.beginLoading();
    if (this.externalAppStateSubscription$) {
      this.ngOnDestroy();
    }
    this.externalAppStateSubscription$ = this.getFeaturePermissions(municipalityGuid).subscribe((values: FeaturePermission[]) => this.add(values));
  }

  public addApp(value: FeaturePermission): void {
    if (!isNil(value)) {
      let formGroup = new FormGroup({ status: new FormControl(PermissionStatusCode.DEACTIVATEDALL) });
      value.formGroup = formGroup;
      value.externeAppsStatus = cloneDeep(externeAppsDefaultStatus);
      value = this.mapFeaturePermission(value, this.userMunicipalitiesService.municipalities, [value], true);
      let featureAppToSet = [...this.state.values, value];
      featureAppToSet = this.sortFeaturePermissions(featureAppToSet);
      this.setState({ ...this.state, values: featureAppToSet });
    }
  }

  public updateApp(permission: FeaturePermission): void {
    if (!isNil(permission)) {
      const filteredPermission = this.state.values.filter((value: FeaturePermission) => value.guid === permission.guid);
      if (filteredPermission) {
        permission.formGroup = new FormGroup({ status: new FormControl(permission.status) });
        const features: FeaturePermission[] = this.state.values.map((value: FeaturePermission) => {
          if (value.guid === permission.guid) {
            value.germanName = permission.germanName;
            value.genericExternalURL = permission.genericExternalURL;
            value.iconName = permission.iconName;
            value.featureFunction = permission.featureFunction;
            value.modifyDate = permission.modifyDate;
            value.modifyUser = permission.modifyUser;
            value.isGenericLink = permission.isGenericLink;
            value.isTokenRequired = permission.isTokenRequired;
            value.municipalityRestrictions = permission.municipalityRestrictions;
            value = this.mapFeaturePermission(value, this.userMunicipalitiesService.municipalities, [permission]);
          }
          return value;
        });
        const sortedFeatures = this.sortFeaturePermissions(features);
        this.setState({ ...this.state, values: sortedFeatures });
      }
    }
  }

  public deleteApp(permission: FeaturePermission): void {
    if (!isNil(permission)) {
      const filterPermission = this.state.values.filter((value: FeaturePermission) => value.guid !== permission.guid);
      const sortedFeatures = this.sortFeaturePermissions(filterPermission);
      this.setState({ ...this.state, values: sortedFeatures });
    }
  }

  public updateIndividualLink(guid: string, link: string): void {
    if (!isNil(guid)) {
      const filteredPermission = this.state.values.filter((value: FeaturePermission) => value.guid === guid);
      if (filteredPermission) {
        const features: FeaturePermission[] = this.state.values.map((value: FeaturePermission) => {
          if (value.guid === guid) {
            if (value.municipalityPermissionsMappings?.length > 0) {
              value.municipalityPermissionsMappings[0].applicationURL = link;
            } else {
              value.municipalityPermissionsMappings.push({ applicationURL: link });
            }
          }
          return value;
        });
        this.setState({ ...this.state, values: features });
      }
    }
  }

  public updateMunicipalityRestriction(permissionId: string, restriction: MunicipalityRestriction): void {
    if (!isNil(permissionId)) {
      const filteredPermission = this.state.values.filter((value: FeaturePermission) => value.guid === permissionId);
      if (filteredPermission) {
        const features: FeaturePermission[] = this.state.values.map((value: FeaturePermission) => {
          if (value.guid === permissionId) {
            value.municipalityRestrictions = [restriction as MunicipalityRestrictionsMapping];
          }
          return value;
        });
        this.setState({ ...this.state, values: features });
      }
    }
  }

  public removeMunicipalityRestriction(permissionId: string): void {
    if (!isNil(permissionId)) {
      const filteredPermission = this.state.values.filter((value: FeaturePermission) => value.guid === permissionId);
      if (filteredPermission) {
        const features: FeaturePermission[] = this.state.values.map((value: FeaturePermission) => {
          if (value.guid === permissionId) {
            value.municipalityRestrictions = [];
          }
          return value;
        });
        this.setState({ ...this.state, values: features });
      }
    }
  }

  public updateToteaserMode(guid: string): void {
    if (!isNil(guid)) {
      const filteredPermission = this.state.values.filter((value: FeaturePermission) => value.guid === guid);
      if (filteredPermission) {
        const features: FeaturePermission[] = this.state.values.map((value: FeaturePermission) => {
          if (value.guid === guid) {
            if (value.municipalityRestrictions?.length > 0) {
              value.municipalityRestrictions[0].isTeaser = true;
            }
          }
          return value;
        });
        this.setState({ ...this.state, values: features });
      }
    }
  }

  public mapDefaultStatus(externeAppsStatus: PermissionStatus[], isGenericLink: boolean, hasIndividualLink: boolean): PermissionStatus[] {
    externeAppsStatus.forEach((status) => {
      status.disabled = true;
      if (status.code === PermissionStatusCode.DEACTIVATEDBYMUNICIPALITY) {
        return;
      }
      if (!isGenericLink && status.code === PermissionStatusCode.ACTIVATEDALL) {
        return;
      }
      const disabledForIndividualLinks = [PermissionStatusCode.ACTIVATEDBYMUNICIPALITY, PermissionStatusCode.DEACTIVATEDALL];
      if (!isGenericLink && !hasIndividualLink && disabledForIndividualLinks.includes(status.code)) {
        return;
      }

      status.disabled = false;
    });
    return externeAppsStatus;
  }

  private add(values: FeaturePermission[]): void {
    if (!isNil(values) && !isEmpty(values)) {
      this.setState({ ...this.state, values: [...this.state.values, ...values] });
    }
  }

  private getFeaturePermissions(municipalityGuid?: string): Observable<FeaturePermission[]> {
    return this.featurePermissionsSubject$.pipe(
      first(),
      switchMap(() => forkJoin([this.permissionService.getFeaturePermissons(municipalityGuid), this.permissionService.getAllRestrictions()])),
      withLatestFrom(this.userMunicipalitiesService.municipalities$),
      map(([[featurePermissions, allActivatedFeatures], municipalities]) =>
        this.mapFeaturePermissions(featurePermissions, municipalities, allActivatedFeatures)
      ),
      map((featurePermissions: FeaturePermission[]) => this.sortFeaturePermissions(featurePermissions)),
      tap(() => this.endLoading()),
      catchError((err, o: Observable<any>) => this.handleDefaultError(err, o))
    );
  }

  private handleDefaultError(error: any, o: Observable<any>): Observable<never> {
    this.setState({ ...this.state, loading: false, values: [] });
    return throwError(() => error);
  }

  private sortFeaturePermissions(featurePermissions: FeaturePermission[]): FeaturePermission[] {
    return orderBy(featurePermissions, [(feature) => feature.germanName.toLowerCase()]);
  }

  private mapFeaturePermissions(
    featurePermissions: FeaturePermission[],
    municipalities: MapStoreMunicipality[],
    allRestrictions: FeaturePermission[]
  ): FeaturePermission[] {
    featurePermissions.forEach((featurePermission) => this.mapFeaturePermission(featurePermission, municipalities, allRestrictions));
    return featurePermissions;
  }

  private mapFeaturePermission(
    featurePermission: FeaturePermission,
    municipalities: MapStoreMunicipality[],
    allRestrictions: FeaturePermission[],
    isNew = false
  ): FeaturePermission {
    let status = this.getFeaturePermissionStatus(featurePermission, municipalities, allRestrictions, isNew);
    let formGroup = new FormGroup({ status: new FormControl(status) });
    featurePermission.status = status;
    featurePermission.formGroup = formGroup;
    featurePermission.externeAppsStatus = this.mapDefaultStatus(
      cloneDeep(externeAppsDefaultStatus),
      featurePermission.isGenericLink,
      isNew ? false : featurePermission.municipalityPermissionsMappings?.length > 0
    );
    if (!isNew) {
      featurePermission.isDropdownDisabled =
        featurePermission.isGenericLink &&
        !this.hasActivated(allRestrictions, featurePermission.guid, municipalities, true, this.userMunicipalitiesService.currentMunicipality.guid) &&
        featurePermission.municipalityRestrictions.some((res) => !res.isTeaser);
    }
    return featurePermission;
  }

  private getFeaturePermissionStatus(
    featurePermission: FeaturePermission,
    municipalities: MapStoreMunicipality[],
    allRestrictions: FeaturePermission[],
    isNew = false
  ): PermissionStatusCode {
    let status = PermissionStatusCode.DEACTIVATEDALL;
    const hasRestrictions = featurePermission.municipalityRestrictions?.length > 0;

    if (isNew) {
      return status;
    }

    if (!hasRestrictions) {
      status = PermissionStatusCode.ACTIVATEDBYMUNICIPALITY;
      if (allRestrictions.filter((activated) => activated.guid === featurePermission.guid)[0]?.municipalityRestrictions?.length === 0) {
        status = PermissionStatusCode.ACTIVATEDALL;
      }
    } else if (featurePermission.municipalityRestrictions.filter((m) => m.isTeaser)?.length > 0) {
      status = this.isTeaserAll(allRestrictions, featurePermission.guid, municipalities)
        ? (status = PermissionStatusCode.TEASERALL)
        : PermissionStatusCode.TEASERBYMUNICIPALITY;
    } else {
      status = this.isDeactivatedAll(allRestrictions, featurePermission.guid, municipalities)
        ? PermissionStatusCode.DEACTIVATEDALL
        : PermissionStatusCode.DEACTIVATEDBYMUNICIPALITY;
    }

    return status;
  }

  private isTeaserAll(permissions: FeaturePermission[], permissionId: string, municipalities: MapStoreMunicipality[]): boolean {
    let isTeaser = true;
    const permission = permissions.find((activated) => activated.guid === permissionId);
    municipalities.forEach((municipality) => {
      const restriction = permission?.municipalityRestrictions.find((res) => res.municipalityId === municipality.guid);
      if (restriction === undefined || !restriction.isTeaser) {
        isTeaser = false;
        return;
      }
    });
    return isTeaser;
  }

  private isDeactivatedAll(permissions: FeaturePermission[], permissionId: string, municipalities: MapStoreMunicipality[]): boolean {
    let isDeactivated = true;
    const permission = permissions.find((activated) => activated.guid === permissionId);
    municipalities.forEach((municipality) => {
      const restriction = permission?.municipalityRestrictions.find((res) => res.municipalityId === municipality.guid);
      if (restriction === undefined || restriction.isTeaser) {
        isDeactivated = false;
        return;
      }
    });
    return isDeactivated;
  }

  private hasActivated(
    permissions: FeaturePermission[],
    permissionId: string,
    municipalities: MapStoreMunicipality[],
    excludeSelf: boolean = false,
    municipalityId: string = ''
  ): boolean {
    let hasActivated = true;
    const permission = permissions.find((activated) => activated.guid === permissionId);
    municipalities.forEach((municipality) => {
      const restriction = permission?.municipalityRestrictions.find((res) => res.municipalityId === municipality.guid);
      if (restriction === undefined && !(excludeSelf && municipality.guid === municipalityId)) {
        hasActivated = false;
        return;
      }
    });
    return hasActivated;
  }
}

export const initialExterneAppsState: ExterneAppsState = {
  loading: false,
  values: [],
};

export interface ExterneAppsState {
  loading: boolean;
  values: FeaturePermission[];
}
